import React, { useEffect, useMemo, useRef, useState } from 'react';
import { searchIcon } from '../chatIcons';
import './SearchableListPicker.css';

export type SearchableListPickerOption<T extends string> = {
  value: T;
  label: string;
  keywords?: string[];
};

export interface SearchableListPickerProps<T extends string> {
  value: T | null;
  options: ReadonlyArray<SearchableListPickerOption<T>>;
  onChange: (next: T) => void;
  defaultValue?: T;
  placeholder?: string;
  className?: string;
}

const normalize = (s: string) => s.trim().toLowerCase();

const SearchableListPicker = <T extends string>({
  value,
  options,
  onChange,
  defaultValue,
  placeholder = 'Search',
  className,
}: SearchableListPickerProps<T>) => {
  const [q, setQ] = useState('');
  const listRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (value != null) return;
    if (defaultValue == null) return;
    const exists = options.some((o) => o.value === defaultValue);
    if (exists) onChange(defaultValue);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const filtered = useMemo(() => {
    const nq = normalize(q);
    if (!nq) return options;
    return options.filter((o) => {
      const hay = [o.label, ...(o.keywords ?? [])].map(normalize).join(' ');
      return hay.includes(nq);
    });
  }, [options, q]);

  useEffect(() => {
    if (!value) return;
    const el = listRef.current?.querySelector<HTMLButtonElement>(`[data-value="${value}"]`);
    el?.scrollIntoView({ block: 'nearest' });
  }, [value]);

  return (
    <div className={`ff-search-picker ${className ?? ''}`.trim()}>
      <div className="ff-search-picker__search">
        <img src={searchIcon} alt="" aria-hidden className="ff-search-picker__icon" />
        <input
          className="ff-search-picker__input"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder={placeholder}
          type="text"
        />
      </div>

      <div className="ff-search-picker__list" ref={listRef} role="listbox" aria-label={placeholder}>
        {filtered.map((o) => {
          const selected = value === o.value;
          return (
            <button
              key={o.value}
              type="button"
              role="option"
              aria-selected={selected}
              data-value={o.value}
              className={`ff-search-picker__row ${selected ? 'ff-search-picker__row--selected' : ''}`}
              onClick={() => onChange(o.value)}
            >
              {o.label}
            </button>
          );
        })}
      </div>
    </div>
  );
};

export default SearchableListPicker;

